import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../../services/api';
import AdminForumDetails from './AdminForumDetails';
import AdminReplyForm from './AdminReplyForm';
import './AdminForumList.css';

const AdminForumList = () => {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [notifiedFilter, setNotifiedFilter] = useState('all');
  const [selectedForum, setSelectedForum] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const fetchForums = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (notifiedFilter !== 'all') params.notified_admin = notifiedFilter === 'notified';
      
      const response = await adminAPI.getForums(params);
      setForums(response.data.data);
    } catch (err) {
      setError('Failed to fetch forums');
      console.error('Error fetching forums:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, notifiedFilter]);

  useEffect(() => {
    fetchForums();
  }, [statusFilter, notifiedFilter, fetchForums]);

  const updateForumStatus = async (forumId, newStatus) => {
    try {
      await adminAPI.updateForumStatus(forumId, newStatus);
      fetchForums(); // Refresh the list
    } catch (err) {
      setError('Failed to update forum status');
      console.error('Error updating forum status:', err);
    }
  };

  const markAsNotified = async (forumId) => {
    try {
      await adminAPI.markForumAsNotified(forumId);
      fetchForums(); // Refresh the list
    } catch (err) {
      setError('Failed to mark as notified');
      console.error('Error marking as notified:', err);
    }
  };

  const toggleCommenting = async (forumId, commentingEnabled) => {
    try {
      await adminAPI.toggleCommenting(forumId, commentingEnabled);
      fetchForums(); // Refresh the list
    } catch (err) {
      setError('Failed to toggle commenting');
      console.error('Error toggling commenting:', err);
    }
  };

  const toggleVoting = async (forumId, votingEnabled) => {
    try {
      await adminAPI.toggleVoting(forumId, votingEnabled);
      fetchForums();
    } catch (err) {
      setError('Failed to toggle voting');
      console.error('Error toggling voting:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { class: 'status-open', text: 'Open' },
      completed: { class: 'status-completed', text: 'Completed' },
      spam: { class: 'status-spam', text: 'Spam' },
      removed: { class: 'status-removed', text: 'Removed' }
    };
    
    const config = statusConfig[status] || { class: 'status-default', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-forum-container">
        <div className="loading">Loading forums...</div>
      </div>
    );
  }

  return (
    <div className="admin-forum-container">
      <div className="admin-forum-header">
        <h1>Forum Management</h1>
        <div className="admin-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowReplyForm(true)}
            disabled={!selectedForum}
          >
            Reply to Selected
          </button>
        </div>
      </div>

      <div className="admin-filters">
        <div className="filter-group">
          <label>Status Filter:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
            <option value="spam">Spam</option>
            <option value="removed">Removed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Notification Status:</label>
          <select 
            value={notifiedFilter} 
            onChange={(e) => setNotifiedFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="notified">Notified</option>
            <option value="unnotified">Unnotified</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="admin-forum-list">
        {forums.length === 0 ? (
          <div className="no-forums">
            <p>No forum questions found.</p>
          </div>
        ) : (
          forums.map((forum) => (
            <div key={forum.id} className="admin-forum-item">
              <div className="forum-item-header">
                <div className="forum-info">
                  <h3 className="forum-title">
                    {forum.title || 'No Title'}
                  </h3>
                  <div className="forum-meta">
                    <span className="user-info">
                      By: {forum.user?.name || 'Unknown User'}
                    </span>
                    <span className="forum-date">
                      {formatDate(forum.created_at)}
                    </span>
                    {!forum.notified_admin && (
                      <span className="unnotified-badge">New</span>
                    )}
                  </div>
                </div>
                <div className="forum-actions">
                  {getStatusBadge(forum.status)}
                  <div className="action-buttons">
                    <select 
                      value={forum.status}
                      onChange={(e) => updateForumStatus(forum.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="open">Open</option>
                      <option value="completed">Completed</option>
                      <option value="spam">Spam</option>
                      <option value="removed">Removed</option>
                    </select>
                    {!forum.notified_admin && (
                      <button
                        onClick={() => markAsNotified(forum.id)}
                        className="btn btn-sm btn-secondary"
                      >
                        Mark Notified
                      </button>
                    )}
                    <button
                      onClick={() => toggleCommenting(forum.id, !forum.commenting_enabled)}
                      className={`btn btn-sm ${forum.commenting_enabled ? 'btn-warning' : 'btn-success'}`}
                      title={forum.commenting_enabled ? 'Disable commenting' : 'Enable commenting'}
                    >
                      <i className={`bi ${forum.commenting_enabled ? 'bi-lock' : 'bi-unlock'}`}></i>
                      {forum.commenting_enabled ? 'Disable Comments' : 'Enable Comments'}
                    </button>
                    <button
                      onClick={() => toggleVoting(forum.id, !forum.voting_enabled)}
                      className={`btn btn-sm ${forum.voting_enabled ? 'btn-warning' : 'btn-success'}`}
                      title={forum.voting_enabled ? 'Disable voting' : 'Enable voting'}
                    >
                      <i className={`bi ${forum.voting_enabled ? 'bi-hand-thumbs-down' : 'bi-hand-thumbs-up'}`}></i>
                      {forum.voting_enabled ? 'Disable Voting' : 'Enable Voting'}
                    </button>
                    <button
                      onClick={() => setSelectedForum(forum)}
                      className="btn btn-sm btn-primary"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="forum-description">
                <p>{forum.description}</p>
              </div>

              {forum.attachments && forum.attachments.length > 0 && (
                <div className="forum-attachments">
                  <h4>Attachments:</h4>
                  <div className="attachment-list">
                    {forum.attachments.map((attachment, index) => (
                      <a 
                        key={index}
                        href={attachment.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="attachment-link"
                      >
                        Attachment {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {forum.admin_replies && forum.admin_replies.length > 0 && (
                <div className="admin-replies">
                  <h4>Admin Replies ({forum.admin_replies.length}):</h4>
                  {forum.admin_replies.map((reply, index) => (
                    <div key={index} className="admin-reply">
                      <div className="reply-header">
                        <strong>Admin Reply</strong>
                        <span className="reply-date">
                          {formatDate(reply.created_at)}
                        </span>
                      </div>
                      <div className="reply-content">
                        <p>{reply.reply_text}</p>
                        {reply.attachments && reply.attachments.length > 0 && (
                          <div className="reply-attachments">
                            {reply.attachments.map((attachment, attIndex) => {
                              const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                              return (
                                <div key={attIndex} className="attachment-item">
                                  {isImage ? (
                                    <div className="image-attachment">
                                      <img 
                                        src={attachment.url} 
                                        alt={`Admin Attachment ${attIndex + 1}`}
                                        className="attachment-image"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                      <a 
                                        href={attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                        style={{display: 'none'}}
                                      >
                                        View Admin Image {attIndex + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <a 
                                      href={attachment.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      {attachment.original_name || `Admin Attachment ${attIndex + 1}`}
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {selectedForum && (
        <AdminForumDetails 
          forum={selectedForum}
          onClose={() => setSelectedForum(null)}
          onUpdate={fetchForums}
        />
      )}

      {showReplyForm && selectedForum && (
        <AdminReplyForm 
          forum={selectedForum}
          onClose={() => setShowReplyForm(false)}
          onSuccess={() => {
            setShowReplyForm(false);
            fetchForums();
          }}
        />
      )}
    </div>
  );
};

export default AdminForumList;
